import React, { useState } from "react";
import { X, Minus, Plus, Trash2, MapPin, Tag, ArrowLeft } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { validateCoupon } from "@/lib/api";
import { toast } from "sonner";
import { createOrder } from "@/lib/api";
import { startRazorpayCheckout, getPaymentConfig } from "@/lib/razorpay";
import { PhoneOtpVerify } from "@/components/PhoneOtpVerify";
import { SmartImage } from "@/components/SmartImage";

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, subtotal, discount, total, updateQty, removeItem, coupon, setCoupon, clear } = useCart();
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState(null);
  const [locLoading, setLocLoading] = useState(false);
  const [payEnabled, setPayEnabled] = useState(false);
  const [rzpMeLink, setRzpMeLink] = useState("");
  const [paying, setPaying] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("prepaid");
  const [phoneVerified, setPhoneVerified] = useState(false);

  const [placing, setPlacing] = useState(false);
  const [placedSuccess, setPlacedSuccess] = useState(null); // { orderId, method }

  const prepaidDiscount = paymentMethod === "prepaid" ? Math.round(subtotal * 0.05 * 100) / 100 : 0;
  const deliveryFee = paymentMethod === "cod" ? 60 : 0;
  const finalTotal = Math.max(0, total - prepaidDiscount + deliveryFee);

  React.useEffect(() => {
    getPaymentConfig().then(cfg => {
      setPayEnabled(!!cfg.enabled);
      setRzpMeLink(cfg.razorpay_me_link || "");
    }).catch(() => {});
  }, []);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    try {
      const res = await validateCoupon(couponInput, subtotal);
      if (res.valid) {
        setCoupon(res);
        toast.success(res.message);
      } else {
        setCoupon(null);
        toast.error(res.message);
      }
    } catch { toast.error("Failed to validate coupon"); }
    finally { setCouponLoading(false); }
  };

  const captureLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Location captured");
        setLocLoading(false);
      },
      () => { toast.error("Could not access location. Please allow location or enter address manually."); setLocLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submitOrder = async () => {
    if (!items.length) return;
    if (!name.trim() || !phone.trim() || !address.trim()) {
      toast.error("Please fill name, phone and address"); return;
    }
    if (!phoneVerified) {
      toast.error("Please verify your phone via OTP first"); return;
    }
    setPlacing(true);
    try {
      const created = await createOrder({
        customer_name: name, phone, address, location,
        items: items.map(i => ({ product_id: i.id, name: i.name, quantity: i.quantity, price: i.price })),
        coupon: coupon?.code || null,
        subtotal, discount: discount + prepaidDiscount, total: finalTotal,
        payment_method: "cod",
        notes: `${notes || ""}${notes ? " | " : ""}Payment: Cash on Delivery${deliveryFee ? ` (+₹${deliveryFee} delivery)` : ""}`,
      });
      toast.success("Order placed! Sugandhi will contact you shortly 💐");
      setPlacedSuccess({ orderId: created?.id, method: "cod" });
      clear();
    } catch (e) {
      toast.error("Could not place order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-[#2D0A22]/30 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
      <aside className="absolute inset-y-0 right-0 w-full max-w-md bg-[#FFFBFB] shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#F4D8E2]">
          {checkoutOpen ? (
            <button
              data-testid="back-to-cart-button"
              onClick={() => setCheckoutOpen(false)}
              className="flex items-center gap-2 text-[#D81B60] hover:text-[#AD1457] font-semibold text-sm"
            >
              <ArrowLeft size={18} /> Back to Cart
            </button>
          ) : (
            <div>
              <h3 className="font-serif text-2xl text-[#2D0A22]">Your Bucket</h3>
              <p className="text-xs text-[#8E6377]">{items.length} item{items.length !== 1 ? "s" : ""}</p>
            </div>
          )}
          <button data-testid="close-cart-button" onClick={() => setIsOpen(false)}
            className="w-9 h-9 rounded-full bg-[#FCE4EC] text-[#D81B60] flex items-center justify-center hover:bg-[#F8BBD0]">
            <X size={18} />
          </button>
        </div>

        {checkoutOpen && (
          <div className="px-5 pt-3">
            <h3 className="font-serif text-xl text-[#2D0A22]">Checkout</h3>
            <p className="text-xs text-[#8E6377]">{items.length} item{items.length !== 1 ? "s" : ""} · ₹{finalTotal.toLocaleString("en-IN")}</p>
          </div>
        )}

        {placedSuccess && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3" data-testid="order-success">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-4xl">🎉</div>
            <h3 className="font-serif text-2xl text-[#2D0A22]">Order Placed!</h3>
            <p className="text-sm text-[#8E6377]">
              Order ID <b className="text-[#D81B60]">#{(placedSuccess.orderId || "").slice(0, 8).toUpperCase()}</b><br />
              Sugandhi Flowers has been notified and will reach out to you shortly on WhatsApp to confirm delivery details 💐
            </p>
            <a
              data-testid="track-order-success-link"
              href={`/order/${placedSuccess.orderId}`}
              onClick={(e) => { e.preventDefault(); setIsOpen(false); window.location.href = `/order/${placedSuccess.orderId}`; }}
              className="mt-1 text-sm text-[#D81B60] underline font-semibold"
            >
              Track or cancel this order →
            </a>
            <button
              data-testid="close-success-button"
              onClick={() => { setPlacedSuccess(null); setCheckoutOpen(false); setIsOpen(false); setName(""); setPhone(""); setAddress(""); setNotes(""); setLocation(null); setPhoneVerified(false); }}
              className="mt-2 px-6 py-2 rounded-full bg-[#D81B60] text-white font-semibold hover:bg-[#AD1457]"
            >
              Continue Shopping
            </button>
          </div>
        )}

        <div className={`flex-1 overflow-y-auto px-5 py-4 space-y-3 ${placedSuccess ? "hidden" : ""}`}>
          {items.length === 0 && (
            <div className="text-center py-20">
              <div className="text-6xl mb-2">🌸</div>
              <p className="font-serif text-lg text-[#2D0A22]">Your bucket is empty</p>
              <p className="text-sm text-[#8E6377] mt-1">Add some crochet love to get started</p>
            </div>
          )}
          {items.map(it => (
            <div key={it.id} data-testid={`cart-item-${it.slug}`} className="flex gap-3 p-2 bg-white rounded-xl border border-[#F4D8E2]">
              <SmartImage src={it.image} alt={it.name} width={160} widths={[80, 120, 160, 240]} sizes="80px" className="w-20 h-20 object-cover rounded-lg" />
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-2">
                  <p className="text-sm font-medium text-[#2D0A22] line-clamp-2">{it.name}</p>
                  <button data-testid={`remove-item-${it.slug}`} onClick={() => removeItem(it.id)} className="text-[#8E6377] hover:text-[#D81B60]"><Trash2 size={16} /></button>
                </div>
                <p className="text-[#D81B60] font-bold mt-1">₹{it.price.toLocaleString("en-IN")}</p>
                <div className="mt-auto flex items-center gap-2">
                  <button onClick={() => updateQty(it.id, it.quantity - 1)} className="w-7 h-7 rounded-full bg-[#FCE4EC] text-[#D81B60] flex items-center justify-center"><Minus size={14} /></button>
                  <span data-testid={`qty-${it.slug}`} className="text-sm font-semibold w-6 text-center">{it.quantity}</span>
                  <button data-testid={`increase-qty-${it.slug}`} onClick={() => updateQty(it.id, it.quantity + 1)} className="w-7 h-7 rounded-full bg-[#FCE4EC] text-[#D81B60] flex items-center justify-center"><Plus size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && !placedSuccess && (
          <div className="border-t border-[#F4D8E2] px-5 py-4 space-y-3 bg-[#FDF2F5]">
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 bg-white rounded-xl border border-[#F4D8E2] px-3">
                <Tag size={16} className="text-[#D81B60]" />
                <input
                  data-testid="coupon-input"
                  value={couponInput} onChange={e => setCouponInput(e.target.value)}
                  placeholder="Coupon (try SUGANDHI10)"
                  className="flex-1 py-2 text-sm bg-transparent outline-none"
                />
              </div>
              <button data-testid="apply-coupon-button" onClick={applyCoupon} disabled={couponLoading}
                className="px-4 rounded-xl bg-[#D81B60] text-white text-sm font-semibold hover:bg-[#AD1457]">
                {couponLoading ? "..." : "Apply"}
              </button>
            </div>
            {coupon && <p className="text-xs text-emerald-600" data-testid="coupon-success">✓ {coupon.code} applied — you save ₹{discount.toLocaleString("en-IN")}</p>}

            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-[#8E6377]"><span>Subtotal</span><span data-testid="cart-subtotal">₹{subtotal.toLocaleString("en-IN")}</span></div>
              {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Coupon</span><span>-₹{discount.toLocaleString("en-IN")}</span></div>}
              {prepaidDiscount > 0 && <div className="flex justify-between text-emerald-600" data-testid="prepaid-discount-row"><span>Prepaid 5% off</span><span>-₹{prepaidDiscount.toLocaleString("en-IN")}</span></div>}
              {deliveryFee > 0 && <div className="flex justify-between text-[#8E6377]" data-testid="cod-delivery-row"><span>COD Delivery</span><span>+₹{deliveryFee}</span></div>}
              <div className="flex justify-between font-bold text-base text-[#2D0A22] pt-1"><span>Total</span><span data-testid="cart-total">₹{finalTotal.toLocaleString("en-IN")}</span></div>
            </div>

            {!checkoutOpen ? (
              <button
                data-testid="proceed-to-checkout-button"
                onClick={() => setCheckoutOpen(true)}
                className="w-full py-3 rounded-xl bg-[#D81B60] hover:bg-[#AD1457] text-white font-semibold shadow-[0_4px_14px_rgba(216,27,96,0.3)]"
              >
                Proceed to Checkout
              </button>
            ) : (
              <div className="space-y-2 pt-2 border-t border-[#F4D8E2]">
                <div>
                  <p className="text-xs font-semibold text-[#2D0A22] mb-2">Payment Method</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      data-testid="pay-method-prepaid"
                      type="button"
                      onClick={() => setPaymentMethod("prepaid")}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${paymentMethod === "prepaid" ? "border-[#D81B60] bg-[#FCE4EC]" : "border-[#F4D8E2] bg-white"}`}
                    >
                      <p className="text-sm font-semibold text-[#2D0A22]">Prepaid</p>
                      <p className="text-[11px] text-emerald-600 font-semibold">Get 5% extra off</p>
                    </button>
                    <button
                      data-testid="pay-method-cod"
                      type="button"
                      onClick={() => setPaymentMethod("cod")}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${paymentMethod === "cod" ? "border-[#D81B60] bg-[#FCE4EC]" : "border-[#F4D8E2] bg-white"}`}
                    >
                      <p className="text-sm font-semibold text-[#2D0A22]">Cash on Delivery</p>
                      <p className="text-[11px] text-[#8E6377]">+₹60 delivery charge</p>
                    </button>
                  </div>
                </div>
                <input data-testid="checkout-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your Name" className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2] bg-white text-sm outline-none focus:border-[#D81B60]" />
                <PhoneOtpVerify
                  phone={phone}
                  onPhoneChange={setPhone}
                  verified={phoneVerified}
                  onVerified={(p) => { setPhone(p); setPhoneVerified(true); }}
                  name={name}
                />
                <textarea data-testid="checkout-address" value={address} onChange={e => setAddress(e.target.value)} placeholder="Delivery Address" rows={2} className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2] bg-white text-sm outline-none focus:border-[#D81B60] resize-none" />
                <input data-testid="checkout-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className="w-full px-3 py-2 rounded-xl border border-[#F4D8E2] bg-white text-sm outline-none focus:border-[#D81B60]" />

                <button data-testid="capture-location-button" onClick={captureLocation} disabled={locLoading}
                  className="w-full py-2 rounded-xl border border-[#D81B60] text-[#D81B60] font-medium flex items-center justify-center gap-2 hover:bg-[#FCE4EC]">
                  <MapPin size={16} />
                  {locLoading ? "Getting location..." : location ? "Location Captured ✓" : "Share My Location (Optional)"}
                </button>

                {paymentMethod === "cod" && (
                  <button data-testid="place-order-cod-button" onClick={submitOrder} disabled={placing}
                    className="w-full py-3 rounded-xl bg-[#D81B60] hover:bg-[#AD1457] text-white font-semibold flex items-center justify-center gap-2 shadow-[0_4px_14px_rgba(216,27,96,0.3)] disabled:opacity-60">
                    {placing ? "Placing order..." : `Place Order — ₹${finalTotal.toLocaleString("en-IN")} (COD)`}
                  </button>
                )}
                {rzpMeLink && paymentMethod === "prepaid" && !payEnabled && (
                  <button
                    data-testid="razorpay-link-button"
                    disabled={placing}
                    onClick={async () => {
                      if (!name.trim() || !phone.trim() || !address.trim()) {
                        toast.error("Please fill name, phone and address"); return;
                      }
                      if (!phoneVerified) { toast.error("Please verify your phone via OTP first"); return; }
                      setPlacing(true);
                      let created = null;
                      try {
                        created = await createOrder({
                          customer_name: name, phone, address, location,
                          items: items.map(i => ({ product_id: i.id, name: i.name, quantity: i.quantity, price: i.price })),
                          coupon: coupon?.code || null,
                          subtotal, discount: discount + prepaidDiscount, total: finalTotal,
                          payment_method: "prepaid",
                          notes: `${notes || ""} | Prepaid via Razorpay Link (5% off -₹${prepaidDiscount})`,
                        });
                      } catch { toast.error("Could not save order — please try again"); setPlacing(false); return; }
                      // Open razorpay.me payment page in new tab
                      window.open(`${rzpMeLink}?amount=${finalTotal.toFixed(2)}`, "_blank");
                      toast.success("Payment page opened in new tab");
                      setPlacedSuccess({ orderId: created?.id, method: "prepaid-link" });
                      clear();
                      setPlacing(false);
                    }}
                    className="w-full py-3 rounded-xl bg-[#D81B60] hover:bg-[#AD1457] text-white font-semibold flex items-center justify-center gap-2 shadow-[0_4px_14px_rgba(216,27,96,0.3)] disabled:opacity-60"
                  >
                    💳 {placing ? "Placing..." : `Pay ₹${finalTotal.toLocaleString("en-IN")} via Razorpay`}
                  </button>
                )}
                {payEnabled && paymentMethod === "prepaid" && (
                  <button
                    data-testid="razorpay-checkout-button"
                    disabled={paying}
                    onClick={async () => {
                      if (!name.trim() || !phone.trim() || !address.trim()) {
                        toast.error("Please fill name, phone and address"); return;
                      }
                      if (!phoneVerified) { toast.error("Please verify your phone via OTP first"); return; }
                      setPaying(true);
                      let orderId = null;
                      try {
                        const created = await createOrder({
                          customer_name: name, phone, address, location,
                          items: items.map(i => ({ product_id: i.id, name: i.name, quantity: i.quantity, price: i.price })),
                          coupon: coupon?.code || null,
                          subtotal, discount: discount + prepaidDiscount, total: finalTotal,
                          payment_method: "prepaid",
                          notes: `${notes} | Prepaid 5% off`,
                        });
                        orderId = created.id;
                      } catch {}
                      await startRazorpayCheckout({
                        amount: finalTotal,
                        customer: { name, phone },
                        notes: { address, coupon: coupon?.code || "" },
                        orderId,
                        onSuccess: () => {
                          toast.success("Payment successful! Thank you 💐");
                          setPlacedSuccess({ orderId, method: "prepaid" });
                          clear();
                        },
                        onFailure: (msg) => toast.error(msg || "Payment failed"),
                      });
                      setPaying(false);
                    }}
                    className="w-full py-3 rounded-xl bg-[#D81B60] hover:bg-[#AD1457] text-white font-semibold flex items-center justify-center gap-2 shadow-[0_4px_14px_rgba(216,27,96,0.3)]"
                  >
                    💳 {paying ? "Processing..." : `Pay ₹${finalTotal.toLocaleString("en-IN")} (Razorpay)`}
                  </button>
                )}
                <p className="text-[11px] text-center text-[#8E6377]">Sugandhi Flowers will contact you on WhatsApp to confirm delivery details after your order is placed.</p>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};
